/**
 * Four counts, and the sentence that says what they counted.
 *
 * Not a chart: four numbers do not need an axis, and `DESIGN.md` §8's cap of three
 * charts above the fold is easiest to respect by drawing none.
 *
 * The important property is the caption. `GET /devices` is offset-paged, so a census
 * over the page is **not** a census over the fleet, and Task 44 spent a whole section
 * on exactly this class of mistake. So `counted` and `total` are both printed, always,
 * and the help text says which is which — rather than printing one number and letting
 * the reader assume it is the other.
 */

import type { ReactNode } from 'react';

import { useI18n } from '../../i18n';
import { FLEET_READINGS, type FleetCensus as Census } from '../../devices/fleet';
import { LivenessBadge, readingLabelKey } from './DeviceBadges';

export function FleetCensusStrip({
  census,
  total,
}: {
  readonly census: Census;
  readonly total: number;
}): ReactNode {
  const { t } = useI18n();
  return (
    <section className="wp-section" aria-labelledby="k5-census-heading" data-testid="k5-census">
      <h2 className="wp-section__title" id="k5-census-heading">
        {t('devices.census.title')}
      </h2>
      {/* Four printed figures, ruled off the page — the first thing the IT persona's
          question ("which devices are unhealthy or unaccounted for?") is answered
          with. Not four cards: it is one reading taken four ways. The badge above
          each number carries the glyph and the word, so the strip is legible in
          greyscale and the number never depends on a colour to be understood. */}
      <ul className="wp-c-figures">
        {FLEET_READINGS.map((reading) => (
          <li key={reading} className="wp-c-figure" data-testid={`k5-census-${reading}`}>
            <p>
              <LivenessBadge reading={reading} testId={`k5-census-badge-${reading}`} />
            </p>
            <p className="wp-c-figure__value">
              <span className="wp-visually-hidden">{t(readingLabelKey(reading))}: </span>
              {census[reading]}
            </p>
          </li>
        ))}
      </ul>
      <p className="wp-small wp-muted" data-testid="k5-census-scope">
        {t('devices.census.scope', { counted: census.counted, total })}
      </p>
      <p className="wp-small wp-muted">{t('devices.census.help')}</p>
    </section>
  );
}
